/* eslint-disable import/prefer-default-export */
/* eslint-disable prefer-object-spread */
import {
    Modifier,
    EditorState,
    ContentBlock,
    genKey,
} from 'draft-js';
import { Map } from 'immutable';
import getContentStateFragment from 'draft-js/lib/getContentStateFragment';
import applyEntityToContentBlock from 'draft-js/lib/applyEntityToContentBlock';
import empty from './createEmptyEditorState';
import { ENTITY_TYPE, BLOCK_TYPE } from './CONSTANTS';

const insertTable = (editorState) => {
    const selection = editorState.getSelection();
    const content = editorState.getCurrentContent();
    const blockKey = editorState.getSelection().getStartKey();
    const blockMap = content.getBlockMap();
    const block = blockMap.get(blockKey);
    const blocksBefore = blockMap.toSeq().takeUntil(v => (v === block));
    const blocksAfter = blockMap.toSeq().skipUntil(v => (v === block)).rest();

    const newBlockKey = genKey();

    const newBlock = new ContentBlock({
        key: newBlockKey,
        type: BLOCK_TYPE.ATOMIC,
        text: '',
        characterList: block.getCharacterList().slice(0, 0),
        depth: 0,
        data: Map({
            rows: [
                [empty(), empty()],
                [empty(), empty()],
            ],
        }),
    });

    const emptyBlockKey = genKey();

    const emptyBlock = new ContentBlock({
        key: emptyBlockKey,
        type: BLOCK_TYPE.UNSTYLED,
        text: '',
    });

    const newBlockMap = blocksBefore.concat(
        [[blockKey, block], [newBlockKey, newBlock], [emptyBlockKey, emptyBlock]],
        blocksAfter,
    ).toOrderedMap();

    const newContent = content.merge({
        blockMap: newBlockMap,
        selectionBefore: selection.merge({
            anchorKey: blockKey,
            anchorOffset: 0,
            focusKey: blockKey,
            focusOffset: 0,
            isBackward: false,
        }),
        selectionAfter: selection.merge({
            anchorKey: newBlockKey,
            anchorOffset: 0,
            focusKey: newBlockKey,
            focusOffset: 0,
            isBackward: false,
        }),
    });

    return EditorState.push(editorState, newContent, 'insert-table');
};

const isInternalTablePaste = (html, editorKey) => (html && html.includes(`data-editor="${editorKey}"`)
            && html.includes('tettra-table'));

const insertPastedTable = (clipboard, editorState) => {
    const contentState = editorState.getCurrentContent();
    const withEntityCopies = clipboard.map((block) => {
        const entityRanges = [];
        block.findEntityRanges(
            character => character.getEntity() !== null,
            (start, end) => {
                entityRanges.push({ start, end });
            },
        );
        return entityRanges.reduce((accBlock, range) => {
            const entity = contentState.getEntity(accBlock.getEntityAt(range.start));
            const contentStateWithEntity = contentState.createEntity(
                entity.getType(),
                entity.getMutability(),
                Object.assign({}, entity.getData()),
            );
            const newEntityKey = contentStateWithEntity.getLastCreatedEntityKey();
            return applyEntityToContentBlock(accBlock, range.start, range.end, newEntityKey);
        }, block);
    });
    const newContent = Modifier.replaceWithFragment(
        contentState,
        editorState.getSelection(),
        withEntityCopies,
    );
    return EditorState.push(
        editorState,
        newContent,
        'insert-fragment',
    );
};

const hasTableInSelection = (editorState) => {
    const content = editorState.getCurrentContent();
    const selection = editorState.getSelection();

    const start = selection.getStartKey();
    const end = selection.getEndKey();

    return content.getBlockForKey(start) != null && content.getBlockForKey(end) != null && getContentStateFragment(content, selection).filter((block) => {
        const entityKey = block.getEntityAt(0);
        const entity = entityKey && content.getEntity(entityKey);
        return entity && entity.type === ENTITY_TYPE.TABLE;
    }).size > 0;
};

export {
    insertTable,
    insertPastedTable,
    isInternalTablePaste,
    hasTableInSelection,
};
